import { API_BASE_URL } from '@synergy/config';
import { Dispatch } from 'redux';
import { DocumentType, UploadState, UploadType } from '@synergy/models';
import { Media } from '@synergy/models';

import { MediaApi } from './media-api';
import { MediaReducerState } from './media-reducers';

export const UPLOAD_FILES_SET = 'UPLOAD_FILE_SET';
export const UPLOAD_FILES_RESET = 'UPLOAD_FILE_RESET';
export const UPLOAD_FILE_START = 'UPLOAD_FILE_START';
export const UPLOAD_FILE_PROGRESS = 'UPLOAD_FILE_PROGRESS';
export const UPLOAD_FILE_SUCCESS = 'UPLOAD_FILE_SUCCESS';
export const UPLOAD_FILE_FAILURE = 'UPLOAD_FILE_FAIL';
export const UPLOAD_FILE_CANCEL = 'UPLOAD_FILE_CANCEL';
export const UPLOAD_FILE_REMOVE = 'UPLOAD_FILE_REMOVE';
export const UPLOAD_FILE_REMOVE_FAILURE = 'UPLOAD_FILE_REMOVE_FAILURE';
export const UPLOAD_FILE_TITLE = 'UPLOAD_FILE_TITLE';

interface PartialState {
    media: MediaReducerState;
}

export const uploadFileStart = (documentType: DocumentType, documentId: number, media: Media) => {
    return {
        type: UPLOAD_FILE_START,
        documentType,
        documentId,
        media,
    };
};

export const uploadFileSuccess = (documentType: DocumentType, documentId: number, media: Media) => {
    return {
        type: UPLOAD_FILE_SUCCESS,
        documentType,
        documentId,
        media,
    };
};

export const uploadFileProgress = (documentType: DocumentType, documentId: number, media: Media, progress: number) => {
    return {
        type: UPLOAD_FILE_PROGRESS,
        documentType,
        documentId,
        media,
        progress,
    };
};

export const uploadFileFailure = (documentType: DocumentType, documentId: number, media: Media) => {
    return {
        type: UPLOAD_FILE_FAILURE,
        documentType,
        documentId,
        media,
    };
};

export const uploadFileCancel = (documentType: DocumentType, documentId: number, media: Media) => {
    return {
        type: UPLOAD_FILE_CANCEL,
        documentType,
        documentId,
        media,
    };
};

export const uploadFileRemove = (documentType: DocumentType, documentId: number, media: Media) => {
    return {
        type: UPLOAD_FILE_REMOVE,
        documentType,
        documentId,
        media,
    };
};

export const uploadFileRemoveFailure = (documentType: DocumentType, documentId: number, media: Media) => {
    return {
        type: UPLOAD_FILE_REMOVE_FAILURE,
        documentType,
        documentId,
        media,
    };
};

export const uploadFileTitle = (id: number, title: string | null) => {
    return {
        type: UPLOAD_FILE_TITLE,
        id,
        title,
    };
};

/** Names a stored file; an empty title takes the name off again. */
export const updateUploadedFileTitle = (media: Media, title: string) => {
    return async (dispatch: Dispatch): Promise<boolean> => {
        const api = new MediaApi(API_BASE_URL);
        const response = await api.updateTitle(media.id, title.trim() || null);

        if (response.success === true) {
            dispatch(uploadFileTitle(media.id, response.result.title ?? null));
        }

        return response.success === true;
    };
};

export const uploadFilesReset = () => {
    return {
        type: UPLOAD_FILES_RESET,
    };
};

export const uploadFilesSet = (media: Media[]) => {
    return {
        type: UPLOAD_FILES_SET,
        media,
    };
};

export const resetUploads = () => {
    return (dispatch: Dispatch) => {
        dispatch(uploadFilesReset());
    };
};

export const setUploads = (media: Media[]) => {
    return (dispatch: Dispatch) => {
        dispatch(uploadFilesSet(media));
    };
};

export const removeUploadedFile = (documentType: DocumentType, id: number, media: Media) => {
    return async (dispatch: Dispatch, _getState: () => PartialState) => {
        if (media.state === UploadState.Failed) {
            dispatch(uploadFileRemove(documentType, id, media));
            return;
        }

        const api = new MediaApi(API_BASE_URL);
        try {
            await api.removeFile(media.id);
            dispatch(uploadFileRemove(documentType, id, media));
        } catch (error) {
            dispatch(uploadFileRemoveFailure(documentType, id, media));
        }
    };
};

/**
 * Puts one file on the server and says whether it got there.
 *
 * The answer is what lets a mask confirm the upload: attachments are stored
 * the moment they are dropped, independently of "Speichern", and without a
 * word nobody could tell (#2230 P1).
 */
export const uploadFile = (
    documentType: DocumentType,
    id: number,
    file: File,
    type = UploadType.Internal,
    title?: string
) => {
    return async (dispatch: Dispatch, _getState: () => PartialState): Promise<boolean> => {
        const media = Media.fromFile(file, type);

        dispatch(uploadFileStart(documentType, id, media));

        const api = new MediaApi(API_BASE_URL);
        const response = await api.addFile(
            documentType,
            id,
            file,
            media.type,
            c => (media.cancel = c),
            progress => {
                const percentCompleted = Math.floor((progress.loaded * 100) / progress.total);
                dispatch(uploadFileProgress(documentType, id, { ...media }, percentCompleted));
            }
        );

        if (response.success === true) {
            dispatch(uploadFileSuccess(documentType, id, response.result));

            // The title is stored on its own: the upload takes files only.
            if (title?.trim()) {
                const titled = await api.updateTitle(response.result.id, title.trim());

                if (titled.success === true) {
                    dispatch(uploadFileTitle(response.result.id, titled.result.title ?? null));
                }
            }

            return true;
        }

        if (response.error instanceof Error) {
            dispatch(uploadFileFailure(documentType, id, media));
        } else {
            dispatch(uploadFileCancel(documentType, id, media));
        }

        return false;
    };
};

export const uploadFiles = (documentType: DocumentType, id: number, files: File[], type = UploadType.Internal) => {
    return async (dispatch: Dispatch, _getState: () => PartialState) => {
        if (files.length === 0) {
            return;
        }

        const medias = files.map(file => Media.fromFile(file, type));
        medias.forEach(media => dispatch(uploadFileStart(documentType, id, media)));

        const api = new MediaApi(API_BASE_URL);
        const response = await api.addFiles(documentType, id, files, type);

        if (response.success === true) {
            response.result.forEach(media => dispatch(uploadFileSuccess(documentType, id, media)));
        } else {
            medias.forEach(media => dispatch(uploadFileFailure(documentType, id, media)));
        }
    };
};
